// apps/landing-page/src/components/personalization/PersonalizationModal.tsx
'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { AudienceSegment } from '@/context/PreferencesContext';
import type { SegmentExamType } from '@/lib/api';
import type { SegmentPolicyStatus } from '@/hooks/useSegmentPolicy';
import { SegmentChipRow } from './SegmentChipRow';
import { InterestChipRow } from './InterestChipRow';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface PersonalizationModalProps {
  open: boolean;
  segment: AudienceSegment | null;
  interests: string[];
  examTypes: SegmentExamType[];
  policyStatus: SegmentPolicyStatus;
  /** True only for the first-visit auto-open path — swaps the header copy to a welcome greeting. */
  greeting?: boolean;
  onClose: () => void;
  onSegmentChange: (segment: AudienceSegment) => void;
  onToggleInterest: (slug: string) => void;
  onClearInterests: () => void;
  onSave: () => void;
  onClear: () => void;
}

// Scroll-triggered / chip-triggered picker — carries the exact same
// segment+interest logic the old inline PersonalizationStrip used, now
// presented as an accessible dialog: bottom sheet on mobile, centered card
// on sm+. See docs/personalization-spec.md — UX surface A + §0.4.
export function PersonalizationModal({
  open,
  segment,
  interests,
  examTypes,
  policyStatus,
  greeting = false,
  onClose,
  onSegmentChange,
  onToggleInterest,
  onClearInterests,
  onSave,
  onClear,
}: PersonalizationModalProps) {
  const t = useTranslations('personalization');
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  // Enter transition + capture the trigger element so focus returns to it.
  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Body scroll lock while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Initial focus + return focus to the trigger on close.
  useEffect(() => {
    if (!open) return undefined;
    const first = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    first?.focus();
    return () => {
      previousFocusRef.current?.focus?.();
    };
  }, [open]);

  // Esc to close + Tab focus trap.
  useEffect(() => {
    if (!open) return undefined;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleBackdropClick = useCallback(() => onClose(), [onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        aria-hidden="true"
        onClick={handleBackdropClick}
        className={cn('absolute inset-0 bg-slate-900/40 transition-opacity duration-200', visible ? 'opacity-100' : 'opacity-0')}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative w-full max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl transition-all duration-200 ease-out sm:max-w-2xl sm:rounded-2xl',
          visible ? 'translate-y-0 opacity-100 sm:scale-100' : 'translate-y-6 opacity-0 sm:translate-y-0 sm:scale-95',
        )}
      >
        {/* Grab handle — mobile bottom-sheet affordance only, no drag gesture attached. */}
        <div className="flex justify-center pt-2.5 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-slate-300" />
        </div>

        {/* Header zone — soft teal tint gives the sheet an intentional brand atmosphere. */}
        <div className="relative overflow-hidden rounded-t-2xl bg-teal-50/40">
          <div className="relative px-6 pt-5 pb-5 sm:px-8 sm:pt-7 sm:pb-6">
            <button
              type="button"
              onClick={onClose}
              aria-label={t('dismissAria')}
              className="absolute top-3 right-3 rounded-full p-1.5 text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-600 cursor-pointer sm:top-4 sm:right-4"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="pr-8">
              <p id={titleId} className="font-display text-lg font-bold text-slate-900 sm:text-xl">
                {greeting ? t('greetingTitle') : t('modalTitle')}
              </p>
              <p className="mt-1.5 text-sm text-slate-500">{greeting ? t('greetingSubtitle') : t('modalSubtitle')}</p>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 sm:px-8 sm:pb-8">
          <div className="space-y-7">
            {!segment ? (
              <div>
                <p className="text-sm text-slate-500 mb-3.5">{t('segmentPrompt')}</p>
                <SegmentChipRow value={segment} onChange={onSegmentChange} />
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center gap-2">
                  <SegmentChipRow value={segment} onChange={onSegmentChange} compact />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-3.5">{t('interestPrompt')}</p>
                  <InterestChipRow
                    examTypes={examTypes}
                    status={policyStatus}
                    value={interests}
                    onToggle={onToggleInterest}
                    onClear={onClearInterests}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-5">
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                {t('skipAction')}
              </button>
              <div className="flex items-center gap-4">
                {segment && (
                  <button
                    type="button"
                    onClick={onClear}
                    className="text-sm font-semibold text-slate-600 hover:text-teal-700 transition-colors cursor-pointer"
                  >
                    {t('clearAction')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onSave}
                  className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors cursor-pointer"
                >
                  {t('saveAction')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
